import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, ChevronRight, Minus, Plus, CheckCircle2, Truck, Shield, RotateCcw, Dumbbell } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;
  const relatedProducts = product 
    ? products.filter(p => p.categoryId === product.categoryId && p.id !== product.id).slice(0, 4)
    : [];

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Active Onyx Fit`;
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <CartDrawer />
        <main className="pt-[104px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <Dumbbell className="w-20 h-20 text-dark-600 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-4">Product Not Found</h1>
            <p className="text-dark-400 mb-8">The product you're looking for doesn't exist.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors">
              Back to Shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Breadcrumb */}
        <div className="bg-dark-900 border-b border-dark-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-dark-400 hover:text-athletic-400 transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-dark-600" />
              <Link to="/shop" className="text-dark-400 hover:text-athletic-400 transition-colors">Shop</Link>
              <ChevronRight className="w-4 h-4 text-dark-600" />
              <Link to={`/shop?category=${category?.slug}`} className="text-dark-400 hover:text-athletic-400 transition-colors">
                {category?.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-dark-600" />
              <span className="text-white font-medium truncate max-w-[200px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square bg-dark-900 rounded-2xl overflow-hidden border border-dark-800">
                <img 
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.badge && (
                <span className="absolute top-4 left-4 px-4 py-2 bg-athletic-500 text-white text-sm font-bold uppercase tracking-wider rounded-xl shadow-lg">
                  {product.badge}
                </span>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-6">
                <Link 
                  to={`/shop?category=${category?.slug}`}
                  className="inline-flex items-center gap-2 text-athletic-400 text-sm font-semibold uppercase tracking-wider hover:text-athletic-300 transition-colors mb-3"
                >
                  <Dumbbell className="w-4 h-4" />
                  {category?.name}
                </Link>
                <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-white mb-4">
                  {product.name}
                </h1>
                {product.type && (
                  <p className="inline-block px-3 py-1 mb-3 bg-athletic-500/10 text-athletic-400 text-sm font-semibold rounded-lg border border-athletic-500/20">
                    {product.type}
                  </p>
                )}
                <p className="text-dark-300 text-lg leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Price */}
              <div className="flex items-center gap-4 mb-8">
                <span className="text-4xl font-bold text-white">${product.price.toFixed(2)}</span>
                <span className="px-3 py-1.5 bg-athletic-500/10 text-athletic-400 text-sm font-semibold rounded-lg border border-athletic-500/20">
                  In Stock
                </span>
              </div>

              {/* Key Benefits */}
              {product.keyBenefits && (
                <div className="mb-8">
                  <h3 className="font-display font-bold text-white mb-4">Key Benefits</h3>
                  <ul className="space-y-3">
                    {product.keyBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-athletic-400 mt-0.5 flex-shrink-0" />
                        <span className="text-dark-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* How to Use */}
              {product.usage && (
                <div className="mb-8 bg-dark-900 border border-dark-800 rounded-xl p-6">
                  <h3 className="font-display font-bold text-white mb-2">How to Use</h3>
                  <p className="text-dark-300 text-sm">{product.usage}</p>
                </div>
              )}

              {/* Features */}
              {product.features && (
                <div className="mb-8">
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, i) => (
                      <span key={i} className="px-3 py-1.5 bg-dark-800 text-dark-300 text-sm rounded-lg border border-dark-700">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="flex items-center bg-dark-900 border border-dark-800 rounded-xl overflow-hidden">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    className="p-4 text-dark-400 hover:text-white hover:bg-dark-800 transition-colors"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-16 text-center text-white font-semibold text-lg">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="p-4 text-dark-400 hover:text-white hover:bg-dark-800 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 font-bold uppercase tracking-wider rounded-xl transition-all ${
                    addedToCart 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gradient-to-r from-athletic-500 to-athletic-600 hover:from-athletic-400 hover:to-athletic-500 text-white shadow-lg hover:shadow-glow-athletic'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center p-4 bg-dark-900 rounded-xl border border-dark-800">
                  <Truck className="w-6 h-6 text-athletic-400 mb-2" />
                  <span className="text-white text-sm font-semibold">Fast Shipping</span>
                  <span className="text-dark-500 text-xs">Free over $75</span>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-dark-900 rounded-xl border border-dark-800">
                  <Shield className="w-6 h-6 text-athletic-400 mb-2" />
                  <span className="text-white text-sm font-semibold">Secure Checkout</span>
                  <span className="text-dark-500 text-xs">SSL Protected</span>
                </div>
                <div className="flex flex-col items-center text-center p-4 bg-dark-900 rounded-xl border border-dark-800">
                  <RotateCcw className="w-6 h-6 text-athletic-400 mb-2" />
                  <span className="text-white text-sm font-semibold">30-Day Returns</span>
                  <span className="text-dark-500 text-xs">Hassle-free</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="font-display text-2xl font-bold text-white mb-8">Related Products</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map(relProduct => (
                  <Link 
                    key={relProduct.id}
                    to={`/product/${relProduct.slug}`}
                    className="group bg-dark-900 rounded-2xl overflow-hidden border border-dark-800 hover:border-athletic-500/50 transition-all"
                  >
                    <div className="aspect-square bg-dark-800 relative overflow-hidden">
                      <img 
                        src={relProduct.image}
                        alt={relProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-2 group-hover:text-athletic-400 transition-colors line-clamp-2">
                        {relProduct.name}
                      </h3>
                      <span className="text-xl font-bold text-white">${relProduct.price.toFixed(2)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
